import * as React from 'react';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { ContextualMenu, Dialog, DialogFooter, DialogType, PrimaryButton } from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { formStyles } from 'styles/formStyles';
import {
    addAngebotPosition,
    AngebotPosition,
    defaultAngebotPosition,
    updateAngebotPosition,
    useAngebotPositionList,
} from 'api/angebotPosition';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import { cloneDeep, isEqual } from 'lodash';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { EinheitByCode } from 'formComponents/lookups/EinheitByCode';
import { FormTextarea, FormTextareaOptions } from 'formComponents/FormTextarea';
import { Timestamps } from 'formComponents/Timestamps';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { ArtikelDetailKalkulation, getArtikelDetailKalkulationen } from 'api/artikelDetailKalkulation';
import { getMwstCode, Kunde } from 'api/kunde';
import { FormDecimalField, FormDecimalFieldOptions } from 'formComponents/FormDecimalField';
import { logDifference } from 'utils/helper';
import { ArtikelDetailSelect } from 'components/Lager/ArtikelDetail/ArtikelDetailSelect';

const styles = mergeStyleSets({
    artikelLine: {
        marginTop: 5,
        display: 'grid',
        // gridTemplateColumns: '80px 1fr 3fr',
        gridTemplateColumns: '80px 1fr',
        gridColumnGap: 10,
    },
    textLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '1fr max-content',
        gridColumnGap: 10,
    },
    posLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gridColumnGap: 10,
    },
    lastLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '3fr 1fr',
        gridColumnGap: 10,
    },
    varianteHeader: {
        marginTop: 15,
        textAlign: 'center',
        fontSize: 14,
        fontWeight: 700,
        backgroundColor: '#EEEEEE',
        paddingTop: 10,
    },
    variante: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gridColumnGap: 10,
        backgroundColor: '#EEEEEE',
        padding: '0 10px 10px 10px',
    },
});

interface AngebotPositionEditProps {
    kunde: Kunde;
    aNr: string;
    pos: number; // -1 = new
    readOnly?: boolean;
    onClose: (refetch: boolean) => void;
}

export const AngebotPositionEdit: React.FC<AngebotPositionEditProps> = ({
    kunde,
    aNr,
    pos,
    readOnly = false,
    onClose,
}) => {
    const auth = useSelector(authSelector);

    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const isMounted = useIsMounted();

    const angebotPositionList = useAngebotPositionList(
        {
            aNr,
            pos,
        },
        { enabled: pos !== -1 },
    );

    const [artikelDetailKalkulationen, setArtikelDetailKalkulationen] = React.useState<
        ArtikelDetailKalkulation[] | null
    >(null);

    const [angebotPosition, setAngebotPosition] = React.useState<AngebotPosition | null>(null);

    React.useEffect(() => {
        if (!angebotPositionList.isLoading && !angebotPositionList.isFetching && angebotPosition === null) {
            if (angebotPositionList.data?.length === 1) {
                setAngebotPosition(cloneDeep(angebotPositionList.data[0]));
                setSavingState(SavingState.notChanged);

                if (angebotPositionList.data[0].artikelGruppeNr && angebotPositionList.data[0].artikelNr) {
                    getArtikelDetailKalkulationen({
                        artikelGruppeNr: angebotPositionList.data[0].artikelGruppeNr,
                        artikelNr: angebotPositionList.data[0].artikelNr,
                    }).then((newArtikelDetailKalkulationen) =>
                        setArtikelDetailKalkulationen(newArtikelDetailKalkulationen),
                    );
                }
            } else {
                setAngebotPosition({ ...defaultAngebotPosition, aNr, pos, posMwst: getMwstCode(kunde) });
                setSavingState(SavingState.notSaved);
            }
        }
    }, [angebotPosition, angebotPositionList, aNr, pos, auth, kunde]);

    React.useEffect(() => {
        if (
            savingState === SavingState.notChanged &&
            !angebotPositionList.isLoading &&
            !angebotPositionList.isFetching &&
            angebotPositionList.data?.length === 1 &&
            angebotPosition !== null &&
            !isEqual(angebotPositionList.data[0], angebotPosition)
        ) {
            setSavingState(SavingState.notSaved);
            logDifference('angebotPosition', angebotPosition, angebotPositionList.data[0]);
        }
    }, [savingState, angebotPositionList, angebotPosition]);

    React.useEffect(() => {
        if (angebotPosition) {
            if (angebotPosition.posMenge && angebotPosition.posEPreis) {
                const netto = Math.round(angebotPosition.posMenge * angebotPosition.posEPreis * 100) / 100;
                const mwstFactor = angebotPosition.posMwst ? 1 + angebotPosition?.posMwst / 100 : 1;
                const brutto = angebotPosition.posMwst ? Math.round(netto * mwstFactor * 100) / 100 : netto;

                setAngebotPosition({
                    ...angebotPosition,
                    posNetto: netto,
                    posBrutto: brutto,
                });
            } else {
                setAngebotPosition({
                    ...angebotPosition,
                    posNetto: 0,
                    posBrutto: 0,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [angebotPosition?.posMenge, angebotPosition?.posEPreis, angebotPosition?.posMwst]);

    const validate = (): boolean => {
        if (!angebotPosition?.posText?.trim()) {
            setValidationError('Bitte geben Sie einen Text ein.');
            return false;
        }
        if (validationError !== null) {
            setValidationError(null);
        }
        return true;
    };

    const save = () => {
        if (angebotPosition && savingState !== SavingState.saving) {
            if (validate()) {
                setSavingState(SavingState.saving);
                if (angebotPosition.pos === -1) {
                    addAngebotPosition(angebotPosition)
                        .then(() => {
                            setSavingState(SavingState.saved);
                            if (isMounted) {
                                setSavingState(SavingState.notChanged);
                            }
                            onClose(true);
                        })
                        .catch((error) => {
                            console.error(error);
                            setSavingState(SavingState.error);
                        });
                } else {
                    updateAngebotPosition(angebotPosition)
                        .then(() => {
                            setSavingState(SavingState.saved);
                            if (isMounted) {
                                setSavingState(SavingState.notChanged);
                            }
                            onClose(true);
                        })
                        .catch((error) => {
                            console.error(error);
                            setSavingState(SavingState.error);
                        });
                }
            }
        }
    };

    const renderTextField = (column: keyof AngebotPosition, label: string, options: FormTextFieldOptions = {}) => (
        <FormTextField
            value={(angebotPosition?.[column] || '') as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (angebotPosition) {
                    setAngebotPosition({
                        ...angebotPosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderDecimalField = (
        column: keyof AngebotPosition,
        label: string,
        options: FormDecimalFieldOptions = {},
    ) => (
        <FormDecimalField
            value={(angebotPosition?.[column] || '') as number}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (angebotPosition) {
                    setAngebotPosition({
                        ...angebotPosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderTextarea = (column: keyof AngebotPosition, label: string, options: FormTextareaOptions = {}) => (
        <FormTextarea
            value={angebotPosition?.[column] as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (angebotPosition) {
                    setAngebotPosition({
                        ...angebotPosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderArtikelDetailSelect = () => (
        <ArtikelDetailSelect
            artikelGruppeNr={angebotPosition?.artikelGruppeNr}
            artikelNr={angebotPosition?.artikelNr}
            disabled={angebotPosition?.pos !== -1}
            onChange={async (newArtikelDetail) => {
                if (angebotPosition) {
                    if (!newArtikelDetail || !newArtikelDetail.artikel) {
                        setAngebotPosition({
                            ...angebotPosition,
                            artikelGruppeNr: defaultAngebotPosition.artikelGruppeNr,
                            artikelNr: defaultAngebotPosition.artikelNr,
                            posText: defaultAngebotPosition.posText,
                            posEkPreis: defaultAngebotPosition.posEkPreis,
                            posEPreis: defaultAngebotPosition.posEPreis,
                            posEinh: defaultAngebotPosition.posEinh,
                            posEinh2: defaultAngebotPosition.posEinh2,
                            posEkPreis2: defaultAngebotPosition.posEkPreis2,
                            posEPreis2: defaultAngebotPosition.posEPreis2,
                            posEinh3: defaultAngebotPosition.posEinh3,
                            posEkPreis3: defaultAngebotPosition.posEkPreis3,
                            posEPreis3: defaultAngebotPosition.posEPreis3,
                            posEinh4: defaultAngebotPosition.posEinh4,
                            posEkPreis4: defaultAngebotPosition.posEkPreis4,
                            posEPreis4: defaultAngebotPosition.posEPreis4,
                        });
                        return;
                    }

                    setArtikelDetailKalkulationen(null);

                    const newArtikelDetailKalkulationen = await getArtikelDetailKalkulationen({
                        artikelGruppeNr: newArtikelDetail.artikelGruppeNr,
                        artikelNr: newArtikelDetail.artikelNr,
                    });

                    setArtikelDetailKalkulationen(newArtikelDetailKalkulationen);

                    const defaultKalkulation = newArtikelDetailKalkulationen.find(
                        (k) => k.eh === newArtikelDetail.artikel?.einheit,
                    );

                    const ekPreis = defaultKalkulation?.ek2 || 0;
                    const ePreis = defaultKalkulation?.vk || 0;

                    setAngebotPosition({
                        ...angebotPosition,
                        artikelGruppeNr: newArtikelDetail.artikelGruppeNr,
                        artikelNr: newArtikelDetail.artikelNr,
                        posText: newArtikelDetail.artikel?.text || '',
                        posEkPreis: ekPreis,
                        posEPreis: ePreis,
                        posEinh: newArtikelDetail.artikel?.einheit || '',
                        posEinh2: defaultAngebotPosition.posEinh2,
                        posEkPreis2: defaultAngebotPosition.posEkPreis2,
                        posEPreis2: defaultAngebotPosition.posEPreis2,
                        posEinh3: defaultAngebotPosition.posEinh3,
                        posEkPreis3: defaultAngebotPosition.posEkPreis3,
                        posEPreis3: defaultAngebotPosition.posEPreis3,
                        posEinh4: defaultAngebotPosition.posEinh4,
                        posEkPreis4: defaultAngebotPosition.posEkPreis4,
                        posEPreis4: defaultAngebotPosition.posEPreis4,
                    });
                }
            }}
        />
    );

    const renderEinheit = (
        column: keyof AngebotPosition,
        ekPreisColumn: keyof AngebotPosition,
        ePreisColumn: keyof AngebotPosition,
        label: string,
    ) => (
        <EinheitByCode
            label={label}
            disabled={savingState === SavingState.saving || readOnly}
            value={(angebotPosition?.[column] as string) || ''}
            menuPlacement="top"
            allowEmpty
            onChange={(newEinheit) => {
                let ekPreis = 0;
                let ePreis = 0;
                if (artikelDetailKalkulationen && newEinheit) {
                    const kalkulation = artikelDetailKalkulationen.find((k) => k.eh === newEinheit);
                    ekPreis = kalkulation?.ek2 || 0;
                    ePreis = kalkulation?.vk || 0;
                }

                if (angebotPosition) {
                    setAngebotPosition({
                        ...angebotPosition,
                        [column]: newEinheit,
                        [ekPreisColumn]: ekPreis,
                        [ePreisColumn]: ePreis,
                    });
                }
            }}
        />
    );

    const renderCheckbox = (column: keyof AngebotPosition, label: string) => (
        <Checkbox
            className={formStyles.checkboxInsideForm}
            label={label}
            disabled={savingState === SavingState.saving || readOnly}
            checked={angebotPosition?.[column] as boolean}
            onChange={(event, checked) => {
                if (angebotPosition) {
                    setAngebotPosition({
                        ...angebotPosition,
                        [column]: checked || false,
                    });
                }
            }}
        />
    );

    return (
        <Dialog
            hidden={false}
            title="Angebotsposition"
            dialogContentProps={{
                type: DialogType.normal,
            }}
            modalProps={{
                isBlocking: true,
                dragOptions: {
                    moveMenuItemText: 'Move',
                    closeMenuItemText: 'Close',
                    menu: ContextualMenu,
                },
            }}
            minWidth={1100}
            onDismiss={() => {
                onClose(false);
            }}
        >
            <form className={formStyles.form}>
                {/*
                <div className={styles.artikelLine}>
                    <div>{renderTextField('pos', 'Position', { numeric: true, disabled: true })}</div>
                    <div className={formStyles.gridCell}>{renderArtikelGruppe()}</div>
                    <div className={formStyles.gridCell}>{renderArtikel()}</div>
                </div>
                */}
                <div className={styles.artikelLine}>
                    <div>{renderTextField('pos', 'Position', { numeric: true, disabled: true })}</div>
                    <div>{renderArtikelDetailSelect()}</div>
                </div>
                <div className={formStyles.line}>
                    <div>{renderTextField('posText', 'Text', { maxLength: 100 })}</div>
                </div>
                <div className={formStyles.line}>
                    <div>{renderCheckbox('posDoku', 'Doku-Druck')}</div>
                </div>
                <div className={formStyles.line}>{renderTextarea('posBem', 'Bemerkung zur Position')}</div>
                <div className={styles.posLine}>
                    <div>{renderEinheit('posEinh', 'posEkPreis', 'posEPreis', 'Einheit')}</div>
                    <div>{renderDecimalField('posMenge', 'Menge', { fixed: 3 })}</div>
                    <div>{renderDecimalField('posEkPreis', 'EK-Preis', { fixed: 3 })}</div>
                    <div>{renderDecimalField('posEPreis', 'VK-Preis', { fixed: 3 })}</div>
                    <div>{renderDecimalField('posNetto', 'VK Netto', { disabled: true })}</div>
                    <div>{renderDecimalField('posMwst', 'MwSt.', { fixed: 0 })}</div>
                    <div>{renderDecimalField('posBrutto', 'VK Brutto', { disabled: true })}</div>
                </div>
                <div className={formStyles.threeColumnsBlock}>
                    <div className={styles.varianteHeader}>Variante 1</div>
                    <div className={styles.varianteHeader}>Variante 2</div>
                    <div className={styles.varianteHeader}>Variante 3</div>
                </div>
                <div className={formStyles.threeColumnsBlock}>
                    <div className={styles.variante}>
                        <div>{renderEinheit('posEinh2', 'posEkPreis2', 'posEPreis2', 'Einheit')}</div>
                        <div>{renderDecimalField('posEkPreis2', 'EK-Preis/Einh', { fixed: 3 })}</div>
                        <div>{renderDecimalField('posEPreis2', 'VK-Preis/Einh', { fixed: 3 })}</div>
                    </div>
                    <div className={styles.variante}>
                        <div>{renderEinheit('posEinh3', 'posEkPreis3', 'posEPreis3', 'Einheit')}</div>
                        <div>{renderDecimalField('posEkPreis3', 'EK-Preis/Einh', { fixed: 3 })}</div>
                        <div>{renderDecimalField('posEPreis3', 'VK-Preis/Einh', { fixed: 3 })}</div>
                    </div>
                    <div className={styles.variante}>
                        <div>{renderEinheit('posEinh4', 'posEkPreis4', 'posEPreis4', 'Einheit')}</div>
                        <div>{renderDecimalField('posEkPreis4', 'EK-Preis/Einh', { fixed: 3 })}</div>
                        <div>{renderDecimalField('posEPreis4', 'VK-Preis/Einh', { fixed: 3 })}</div>
                    </div>
                </div>

                <Timestamps
                    modifiedDate={angebotPosition?.updDat}
                    modifiedDecimalTime={angebotPosition?.updUhr}
                    modifiedUser={angebotPosition?.updUser}
                    showCreated={false}
                />

                {validationError && <div className={formStyles.error}>{validationError}</div>}
                {savingState === SavingState.error && <div className={formStyles.error}>Fehler beim Speichern.</div>}

                <DialogFooter>
                    {savingState === SavingState.saving ? (
                        <PrimaryButton text="Speichere..." disabled />
                    ) : (
                        <PrimaryButton
                            onClick={save}
                            text="Speichern"
                            disabled={savingState === SavingState.notChanged || angebotPositionList.isLoading}
                        />
                    )}
                </DialogFooter>
            </form>
            {(angebotPositionList.isLoading || angebotPositionList.isFetching || !angebotPosition) && (
                <LoadingIndicator />
            )}
        </Dialog>
    );
};
